import React from 'react';
import { useSelector } from 'react-redux';

import { FF_HOLIDAY_BANNER_CN, FF_HOLIDAY_BANNER_VN } from 'constants/features';
import { usePeertransferFlags } from 'hooks/usePeertransferFlags/usePeertransferFlags';
import { isPayingFromChina, isPayingFromVietnam } from 'selectors';
import { useTranslations } from 'utils/translations/useTranslations';

import './HolidayBanner.scss';

function Vietnam() {
  const i18n = useTranslations();

  if (i18n.currentLocale === 'en') {
    return (
      <div className="HolidayBanner">
        <div className="HolidayBanner-content">
          <div className="HolidayBanner-title">Important note:</div>
          <p className="HolidayBanner-description">
            Please be advised that the period from{' '}
            <b>25th January to 2nd February 2025</b> is the{' '}
            <b>Lunar New Year holiday</b> in Vietnam. During this public
            holiday, bank transfer payments received will only be processed, and
            payments&apos; status will only be updated, on or after{' '}
            <b>3rd February 2025</b>. We highly recommend you to plan your
            payment in advance.
            <br />
            We sincerely apologize for any inconvenience caused during this
            period and thank you for your patience. Please do not hesitate to
            visit our{' '}
            <a
              target="_blank"
              href="https://help.flywire.com/hc/en-us"
              rel="noreferrer"
            >
              Help & Support Center
            </a>{' '}
            to reach out to our support team if you have any queries during the
            holiday period.
            <br />
            We wish you a wonderful holiday!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="HolidayBanner">
      <div className="HolidayBanner-content">
        <div className="HolidayBanner-title">Thông báo quan trọng:</div>
        <p className="HolidayBanner-description">
          Xin lưu ý rằng khoảng thời gian từ ngày{' '}
          <b>25 tháng 1 đến ngày 2 tháng 2 năm 2025</b> là ngày nghỉ{' '}
          <b>Tết Nguyên đán</b> ở Việt Nam. Trong kỳ nghỉ lễ này, các khoản
          thanh toán chuyển khoản ngân hàng nhận được sẽ chỉ được xử lý và trạng
          thái thanh toán sẽ chỉ được cập nhật trong hoặc sau{' '}
          <b>ngày 3 tháng 2 năm 2025</b>. Chúng tôi khuyên bạn nên lên kế hoạch
          trước cho khoản thanh toán của mình.
          <br />
          Chúng tôi chân thành xin lỗi vì bất kỳ sự bất tiện nào gây ra trong
          thời gian này và cảm ơn sự kiên nhẫn của bạn. Vui lòng truy cập{' '}
          <a
            target="_blank"
            href="https://help.flywire.com/hc/vi"
            rel="noreferrer"
          >
            Trung tâm hỗ trợ
          </a>{' '}
          của chúng tôi để liên hệ với nhóm hỗ trợ của chúng tôi nếu bạn có bất
          kỳ thắc mắc nào trong thời gian nghỉ lễ.
          <br />
          Chúng tôi chúc bạn một kỳ nghỉ tuyệt vời!
        </p>
      </div>
    </div>
  );
}

function China() {
  const i18n = useTranslations();

  if (i18n.currentLocale === 'en') {
    return (
      <div className="HolidayBanner">
        <div className="HolidayBanner-content">
          <div className="HolidayBanner-title">Important note:</div>
          <p className="HolidayBanner-description">
            Please be advised that Chinese New Year is approaching which may
            impact your payment plan. We highly recommend you to plan your
            payment in advance.
            <br />
            For payments from China: Period from 28th January to 4th February
            2025, payments made during this period (including 27th January 2025)
            will only be processed or updated on or after 5th February 2025.
            <br />
            We sincerely apologize for any inconvenience caused during this
            period and thank you for your patience. Please do not hesitate to
            visit our{' '}
            <a
              target="_blank"
              href="https://help.flywire.com/hc/en-us"
              rel="noreferrer"
            >
              Help & Support Center
            </a>{' '}
            to reach out to our support team if you have any queries during
            holiday period.
            <br />
            We wish you a wonderful holiday!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="HolidayBanner">
      <div className="HolidayBanner-content">
        <div className="HolidayBanner-title">重要提醒:</div>
        <p className="HolidayBanner-description">
          请知悉，因 2025年1月28日至
          2025年2月4日为中国春节节假日，在此期间您所支付的款项（包含_1月27日_）将仅会在
          2025年2月5日 或之后开始处理，并更新付款状态。
          <br />
          您可合理安排您的付款计划，以避免出现费用缴纳的延误。由此造成的不便，我们深表歉意，并感谢您的耐心等待。如您在节假日期间有关于付款的问题，您仍可通过“
          <a
            target="_blank"
            href="https://help.flywire.com/hc/zh-cn"
            rel="noreferrer"
          >
            帮助页面
          </a>
          ”联系我们。
          <br />
          飞汇在此提前祝您假期愉快！
        </p>
      </div>
    </div>
  );
}

China.displayName = 'HolidayBanner.China';
Vietnam.displayName = 'HolidayBanner.Vietnam';

function HolidayBanner() {
  const fromChina = useSelector(isPayingFromChina);
  const fromVietnam = useSelector(isPayingFromVietnam);

  const shouldDisplayHolidayBanner = fromChina || fromVietnam;

  const { data: flags } = usePeertransferFlags(
    [FF_HOLIDAY_BANNER_CN, FF_HOLIDAY_BANNER_VN],
    {
      enabled: shouldDisplayHolidayBanner,
    },
  );

  if (!flags) {
    return null;
  }

  const showChineseBanner = fromChina && flags[FF_HOLIDAY_BANNER_CN];
  const showVietnameseBanner = fromVietnam && flags[FF_HOLIDAY_BANNER_VN];

  return (
    <>
      {showChineseBanner && <China />}
      {showVietnameseBanner && <Vietnam />}
    </>
  );
}

export { HolidayBanner };
